/**
 * Theme Store - Zustand store for theme management
 * Sprint 31: Theming & accessibility
 *
 * Manages light/dark theme preference with system preference detection.
 * Persists theme choice to localStorage.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  /** User-selected theme mode */
  mode: ThemeMode;
  /** Resolved theme (light or dark based on mode and system preference) */
  resolved: 'light' | 'dark';
  /** Set theme mode */
  setMode: (mode: ThemeMode) => void;
  /** Toggle between light and dark (ignores system) */
  toggle: () => void;
  /** Initialize theme based on system preference */
  init: () => void;
  /** Initialize from localStorage (call before React renders) */
  initFromStorage: () => void;
}

/**
 * Detect system color scheme preference
 */
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Apply theme class to document root
 */
function applyTheme(theme: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * Resolve theme mode to actual theme
 */
function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'dark',
      resolved: 'dark',

      setMode: (mode: ThemeMode) => {
        const resolved = resolveTheme(mode);
        applyTheme(resolved);
        set({ mode, resolved });
      },

      toggle: () => {
        const current = get().resolved;
        const next = current === 'light' ? 'dark' : 'light';
        applyTheme(next);
        set({ mode: next, resolved: next });
      },

      init: () => {
        const { mode } = get();
        const resolved = resolveTheme(mode);
        applyTheme(resolved);
        set({ resolved });

        // Listen for system preference changes
        if (typeof window !== 'undefined' && mode === 'system') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          const handler = (e: MediaQueryListEvent) => {
            if (get().mode === 'system') {
              const newResolved = e.matches ? 'dark' : 'light';
              applyTheme(newResolved);
              set({ resolved: newResolved });
            }
          };
          mediaQuery.addEventListener('change', handler);
        }
      },

      initFromStorage: () => {
        // Called before React renders to prevent flash of wrong theme
        // Zustand persist middleware handles localStorage, but we need to
        // apply the theme immediately before hydration completes
        const stored = localStorage.getItem('vortex-theme');
        if (stored) {
          try {
            const { state } = JSON.parse(stored);
            if (state?.mode) {
              const resolved = resolveTheme(state.mode);
              applyTheme(resolved);
              set({ mode: state.mode, resolved });
            }
          } catch {
            // Invalid JSON, apply default (dark)
            applyTheme('dark');
          }
        } else {
          // No stored preference, use dark as default
          applyTheme('dark');
        }
      },
    }),
    {
      name: 'vortex-theme',
      partialize: (state) => ({ mode: state.mode }),
    }
  )
);
